import React, { useState, useRef } from 'react';
import { CustomerReview } from '../../types/store';
import { useReviews } from '../../context/ReviewsContext';
import { StarRating } from '../common/StarRating';
import { PAKISTANI_CITIES } from '../../data/mockData';
import {
  CheckCircle2,
  MessageCircleHeart,
  MapPin,
  Camera,
  Heart,
  X,
  Plus,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  ZoomIn,
  Send,
  Upload,
} from 'lucide-react';

export const ReviewsSection: React.FC = () => {
  const { reviews, addReview, likeReview } = useReviews();
  const [activeTab, setActiveTab] = useState<'all' | 'with-photos' | 'top-rated'>('all');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isWriteModalOpen, setIsWriteModalOpen] = useState(false);

  // Review Form State
  const [newAuthor, setNewAuthor] = useState('');
  const [newCity, setNewCity] = useState('Lahore');
  const [newRating, setNewRating] = useState(5);
  const [newFlavor, setNewFlavor] = useState('Vanilla');
  const [newReviewText, setNewReviewText] = useState('');
  const [newReviewImage, setNewReviewImage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Filter reviews
  const filteredReviews = reviews.filter((r) => {
    if (activeTab === 'with-photos') return Boolean(r.reviewImage || r.avatarUrl);
    if (activeTab === 'top-rated') return r.rating >= 5;
    return true;
  });

  // Handle Image File Upload for new review
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPG, PNG, WEBP)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      if (base64) {
        setNewReviewImage(base64);
      }
    };
    reader.readAsDataURL(file);
  };

  // Submit Review Handler
  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAuthor.trim() || !newReviewText.trim()) return;

    setIsSubmitting(true);
    try {
      await addReview({
        author: newAuthor.trim(),
        city: newCity,
        rating: newRating,
        date: 'Just now',
        productName: `MalRich Healthy Weight Gain — ${newFlavor}`,
        flavor: newFlavor,
        reviewText: newReviewText.trim(),
        verifiedBuyer: true,
        reviewImage: newReviewImage || undefined,
        likesCount: 1,
        featured: true,
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setIsWriteModalOpen(false);
        setNewAuthor('');
        setNewReviewText('');
        setNewReviewImage('');
      }, 1800);
    } catch (err) {
      console.error('Error submitting review:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manual scroll controls for track
  const handleManualScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -380 : 380;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Duplicated list for seamless infinite loop marquee
  const loopList = [...filteredReviews, ...filteredReviews, ...filteredReviews];

  return (
    <section
      id="customer-reviews-social-proof"
      className="py-14 sm:py-20 bg-gradient-to-b from-[#F8FAFC] via-[#F1F5F9] to-[#FFFFFF] border-t border-slate-200 overflow-hidden relative"
    >
      {/* Decorative subtle background elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl pointer-events-none -z-0" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl pointer-events-none -z-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/10 to-amber-600/15 text-amber-900 border border-amber-200/70 text-xs font-black px-3.5 py-1.5 rounded-full uppercase tracking-wider mb-3">
              <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
              <span>100% Real Pakistani Customer Feedback</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#111827] tracking-tight">
              Customer Reviews & Experiences
            </h2>
            
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200/80 px-2.5 py-1 rounded-lg">
                <StarRating rating={5.0} showScore={false} size="sm" />
                <span className="text-xs font-black text-amber-950">4.9 / 5.0</span>
              </div>
              <span className="text-xs font-medium text-slate-500">
                Based on <strong className="text-slate-800 font-bold">2,400+ Verified Orders</strong> across Pakistan
              </span>
              <span className="hidden sm:inline-block w-1.5 h-1.5 rounded-full bg-slate-300" />
              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                <span>Original MalRich Delivery Guaranteed</span>
              </span>
            </div>
          </div>

          {/* Action Buttons: Write Review & Tab Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Filter Chips */}
            <div className="inline-flex bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  activeTab === 'all'
                    ? 'bg-[#191975] text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All Reviews ({reviews.length})
              </button>
              <button
                onClick={() => setActiveTab('with-photos')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === 'with-photos'
                    ? 'bg-[#191975] text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Camera className="w-3.5 h-3.5" />
                <span>With Photos</span>
              </button>
            </div>

            {/* Write a Review Button */}
            <button
              id="write-review-btn"
              onClick={() => setIsWriteModalOpen(true)}
              className="inline-flex items-center gap-2 bg-[#191975] hover:bg-[#121258] text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all active:scale-[0.98] cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Write Review</span>
            </button>
          </div>
        </div>

        {/* Continuous Flow Notice & Controls */}
        <div className="flex items-center justify-between text-xs text-slate-500 mb-3 px-1">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="font-semibold text-slate-600">
              Continuous Live Flow (Hover to pause, Click photo to zoom)
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPaused(!isPaused)}
              title={isPaused ? 'Resume scrolling' : 'Pause scrolling'}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
            >
              {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={() => handleManualScroll('left')}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleManualScroll('right')}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* CONTINUOUS MARQUEE CAROUSEL ROW 1 (Flowing smoothly leftwards) */}
      <div
        className="w-full relative overflow-hidden py-4 pause-on-hover"
        ref={scrollContainerRef}
      >
        {/* Left & Right gradient fade masks */}
        <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-r from-[#F8FAFC] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-l from-[#F8FAFC] to-transparent z-10 pointer-events-none" />

        <div
          className={`flex gap-5 px-4 ${
            isPaused ? '' : 'animate-marquee-smooth'
          }`}
          style={isPaused ? { animationPlayState: 'paused' } : {}}
        >
          {loopList.map((review, idx) => (
            <div
              key={`${review.id}-loop-${idx}`}
              className="w-[310px] sm:w-[360px] shrink-0 bg-white rounded-2xl p-5 border border-slate-200/90 shadow-2xs hover:shadow-xl hover:border-slate-300 transition-all duration-300 flex flex-col justify-between group"
            >
              <div>
                {/* Header: Rating & Date */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-1.5">
                    <StarRating rating={review.rating} showScore={false} size="sm" />
                    <span className="text-[11px] font-black text-amber-900 bg-amber-50 px-1.5 py-0.5 rounded">
                      {review.rating}.0
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-400 font-medium">
                    {review.date}
                  </span>
                </div>

                {/* Review Text */}
                <p className="text-xs sm:text-[13px] text-[#1E293B] font-medium leading-relaxed mb-3 italic">
                  "{review.reviewText}"
                </p>

                {/* Attached Review Photo or Parcel Screenshot if available */}
                {review.reviewImage && (
                  <div
                    onClick={() => setSelectedImage(review.reviewImage || null)}
                    className="relative w-full h-36 rounded-xl overflow-hidden mb-3 border border-slate-100 bg-slate-50 cursor-pointer group/img"
                  >
                    <img
                      src={review.reviewImage}
                      alt="Customer Review Photo"
                      width="320"
                      height="144"
                      className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-300"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <span className="bg-black/60 backdrop-blur-xs px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1">
                        <ZoomIn className="w-3 h-3" /> Click to Zoom
                      </span>
                    </div>
                    <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[9px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                      <Camera className="w-2.5 h-2.5" /> Customer Photo
                    </span>
                  </div>
                )}
              </div>

              {/* Bottom Customer Info & Product Tag */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  {/* Customer Avatar */}
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#191975] to-[#2563EB] text-white font-black text-xs flex items-center justify-center overflow-hidden shrink-0 border border-slate-200">
                    {review.avatarUrl ? (
                      <img
                        src={review.avatarUrl}
                        alt={review.author}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      review.author.charAt(0).toUpperCase()
                    )}
                  </div>

                  <div>
                    <h4 className="text-xs font-extrabold text-[#111827] flex items-center gap-1">
                      <span>{review.author}</span>
                      {review.verifiedBuyer && (
                        <span
                          className="inline-flex items-center text-emerald-600"
                          title="Verified Purchaser in Pakistan"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 fill-emerald-100 text-emerald-600" />
                        </span>
                      )}
                    </h4>
                    <div className="flex items-center gap-1 text-[10px] text-slate-500">
                      <MapPin className="w-2.5 h-2.5 text-slate-400" />
                      <span>{review.city || 'Pakistan'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span className="text-[10px] font-extrabold text-[#191975] bg-blue-50/80 border border-blue-100 px-2 py-0.5 rounded-md">
                    {review.flavor || 'Vanilla'}
                  </span>
                  <button
                    onClick={() => likeReview(review.id)}
                    className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                    title="Helpful Review"
                  >
                    <Heart className="w-3 h-3 hover:fill-rose-500" />
                    <span>{review.likesCount || 0}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CONTINUOUS MARQUEE CAROUSEL ROW 2 (Flowing in opposite direction for visual depth) */}
      <div className="w-full relative overflow-hidden py-2 pause-on-hover hidden sm:block">
        <div
          className={`flex gap-5 px-4 ${
            isPaused ? '' : 'animate-marquee-smooth-reverse'
          }`}
          style={isPaused ? { animationPlayState: 'paused' } : {}}
        >
          {loopList.slice().reverse().map((review, idx) => (
            <div
              key={`${review.id}-reverse-${idx}`}
              className="w-[320px] shrink-0 bg-white/90 backdrop-blur-xs rounded-2xl p-4 border border-slate-200 shadow-2xs hover:shadow-lg transition-all flex items-center justify-between gap-3 group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden border border-slate-200 shrink-0">
                  {review.avatarUrl ? (
                    <img
                      src={review.avatarUrl}
                      alt={review.author}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#191975] text-white flex items-center justify-center font-bold text-xs">
                      {review.author.charAt(0)}
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-bold text-[#111827]">{review.author}</span>
                    <StarRating rating={review.rating} showScore={false} size="sm" />
                  </div>
                  <p className="text-[11px] text-slate-600 font-medium line-clamp-1 max-w-[170px]">
                    "{review.reviewText}"
                  </p>
                </div>
              </div>

              <div className="text-right shrink-0">
                <span className="text-[10px] font-bold text-[#191975] bg-blue-50 px-2 py-0.5 rounded">
                  {review.city}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* LIGHTBOX MODAL FOR CUSTOMER REVIEW IMAGES */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl p-2 animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 bg-black/60 hover:bg-black text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-full max-h-[75vh] flex items-center justify-center overflow-hidden rounded-xl bg-slate-900">
              <img
                src={selectedImage}
                alt="Enlarged Customer Review"
                className="w-full h-auto max-h-[75vh] object-contain"
                referrerPolicy="no-referrer"
              />
            </div>
            <div className="p-3 text-center">
              <span className="text-xs font-bold text-slate-700 flex items-center justify-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                Verified Customer Product Parcel / Result Image
              </span>
            </div>
          </div>
        </div>
      )}

      {/* WRITE A REVIEW MODAL */}
      {isWriteModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8 animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="bg-[#191975] text-white p-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <MessageCircleHeart className="w-5 h-5 text-amber-400" />
                  <span>Share Your Experience</span>
                </h3>
                <p className="text-xs text-indigo-200 mt-0.5">
                  Help other Pakistani families choose the right MalRich supplement
                </p>
              </div>
              <button
                onClick={() => setIsWriteModalOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            {submitSuccess ? (
              <div className="p-8 text-center space-y-3">
                <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-[#111827]">Thank You For Your Review!</h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Your feedback and photos have been posted and are now displayed on the store.
                </p>
              </div>
            ) : (
              <form onSubmit={handleReviewSubmit} className="p-6 space-y-4">
                {/* Rating Select */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Your Overall Rating
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        type="button"
                        key={star}
                        onClick={() => setNewRating(star)}
                        className={`text-2xl transition-transform hover:scale-110 cursor-pointer ${
                          star <= newRating ? 'text-amber-400' : 'text-slate-200'
                        }`}
                      >
                        ★
                      </button>
                    ))}
                    <span className="text-xs font-bold text-slate-700 ml-2">
                      {newRating} of 5 Stars
                    </span>
                  </div>
                </div>

                {/* Name & City */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={newAuthor}
                      onChange={(e) => setNewAuthor(e.target.value)}
                      placeholder="e.g. Bilal Ahmed"
                      className="w-full px-3.5 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#191975]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                      City in Pakistan *
                    </label>
                    <select
                      value={newCity}
                      onChange={(e) => setNewCity(e.target.value)}
                      className="w-full px-3.5 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#191975] bg-white"
                    >
                      {PAKISTANI_CITIES.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Flavor Purchased */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Product / Flavor Used
                  </label>
                  <select
                    value={newFlavor}
                    onChange={(e) => setNewFlavor(e.target.value)}
                    className="w-full px-3.5 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#191975] bg-white"
                  >
                    <option value="Vanilla">MalRich Vanilla (Weight Gain)</option>
                    <option value="Mango">MalRich Mango (Balanced Diet)</option>
                    <option value="Chocolate">MalRich Chocolate (Weight Gain)</option>
                    <option value="Strawberry">MalRich Strawberry (Weight Gain)</option>
                    <option value="Buy 2 Get 1 Bundle">3-Tin Family Bundle Pack</option>
                  </select>
                </div>

                {/* Review Text */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Your Review & Experience *
                  </label>
                  <textarea
                    rows={3}
                    required
                    value={newReviewText}
                    onChange={(e) => setNewReviewText(e.target.value)}
                    placeholder="Tell us about the taste, packaging, delivery speed, or results you noticed..."
                    className="w-full px-3.5 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#191975]"
                  />
                </div>

                {/* Upload Review Photo */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Attach Photo / Parcel / Screenshot (Optional)
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  
                  {newReviewImage ? (
                    <div className="relative w-full h-28 rounded-xl overflow-hidden border border-slate-300">
                      <img
                        src={newReviewImage}
                        alt="Uploaded preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setNewReviewImage('')}
                        className="absolute top-2 right-2 bg-rose-600 text-white rounded-full p-1 shadow-md hover:bg-rose-700 cursor-pointer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-3 border-2 border-dashed border-slate-300 hover:border-[#191975] rounded-xl flex items-center justify-center gap-2 text-xs font-bold text-slate-600 hover:text-[#191975] transition-colors cursor-pointer bg-slate-50"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Upload Photo from Phone / Computer</span>
                    </button>
                  )}
                </div>

                {/* Submit button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 rounded-xl bg-[#191975] hover:bg-[#121258] text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-md transition-all cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span>Submitting Review...</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Publish Review</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
